import { useCallback, useEffect, useRef, useState } from "react";

/**
 * State that is controlled when `value !== undefined`, uncontrolled otherwise.
 * `onChange` fires in both modes whenever the value actually changes.
 */
export function useControllableState(value, defaultValue, onChange) {
  const isControlled = value !== undefined;
  const [internal, setInternal] = useState(defaultValue);
  const current = isControlled ? value : internal;

  const currentRef = useRef(current);
  currentRef.current = current;
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  const setValue = useCallback(
    (next) => {
      const resolved = typeof next === "function" ? next(currentRef.current) : next;
      if (Object.is(resolved, currentRef.current)) return;
      if (!isControlled) setInternal(resolved);
      onChangeRef.current?.(resolved);
    },
    [isControlled],
  );

  return [current, setValue];
}

export function useDebouncedValue(value, delay) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    if (!delay) {
      setDebounced(value);
      return undefined;
    }
    const id = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(id);
  }, [value, delay]);
  return debounced;
}
